import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { LoginButton } from '@/components/auth/LoginButton';
import { Badge } from '@/components/ui';

export const LoginPage = () => {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="flex min-h-screen">
      {/* Left Panel - Branding */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-12 dark:from-primary-800 dark:via-primary-900 dark:to-slate-900 lg:flex lg:w-1/2">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -left-4 top-0 h-72 w-72 animate-blob rounded-full bg-white mix-blend-overlay blur-xl filter"></div>
          <div className="animation-delay-2000 absolute -right-4 top-0 h-72 w-72 animate-blob rounded-full bg-white mix-blend-overlay blur-xl filter"></div>
          <div className="animation-delay-4000 absolute -bottom-8 left-20 h-72 w-72 animate-blob rounded-full bg-white mix-blend-overlay blur-xl filter"></div>
        </div>

        {/* Logo and Brand */}
        <div className="relative z-10">
          <div className="mb-8 inline-flex items-center">
            <img
              src="/assets/layout/images/logo-dark.png"
              alt="Tankers International Logo"
              className="h-14 w-auto object-contain"
            />
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="relative z-10 space-y-8">
          <div>
            <h2 className="mb-4 text-4xl font-bold leading-tight text-white">
              Welcome to your
              <br />
              secure workspace
            </h2>
            <p className="text-lg text-primary-100">
              Access all your business tools and collaborate with your team in one unified platform.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm">
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-white">Enterprise Security</h3>
                <p className="text-sm text-primary-100">
                  Protected by Microsoft Azure Active Directory
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm">
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-white">AI-Powered Tools</h3>
                <p className="text-sm text-primary-100">
                  Intelligent chat assistant for enhanced productivity
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm">
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-white">Mobile Ready</h3>
                <p className="text-sm text-primary-100">Access from anywhere, on any device</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-sm text-primary-200">© 2025 Extranet. All rights reserved.</p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex w-full items-center justify-center bg-gray-50 p-8 dark:bg-dark-950 lg:w-1/2">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="mb-8 text-center lg:hidden">
            <div className="mb-4 inline-flex items-center">
              <img
                src="/assets/layout/images/logo-dark.png"
                alt="Tankers International Logo"
                className="h-12 w-auto object-contain"
              />
            </div>
          </div>

          {/* Login Card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl dark:border-dark-700 dark:bg-dark-800">
            <div className="mb-8">
              <Badge variant="primary" className="mb-4">
                Enterprise Sign In
              </Badge>
              <h2 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
                Sign in to your account
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                Use your Microsoft credentials to access the platform
              </p>
            </div>

            {/* Security Notice */}
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
              <div className="flex items-start space-x-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h4 className="mb-1 text-sm font-medium text-blue-900 dark:text-blue-100">
                    Secure Authentication
                  </h4>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Your credentials are verified through Microsoft Azure Active Directory for
                    maximum security.
                  </p>
                </div>
              </div>
            </div>

            {/* Login Button */}
            <div className="space-y-4">
              <LoginButton />

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-dark-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500 dark:bg-dark-800 dark:text-gray-400">
                    Powered by Microsoft
                  </span>
                </div>
              </div>

              {/* Microsoft Logo */}
              <div className="flex justify-center">
                <div className="flex items-center space-x-2 text-gray-400 dark:text-gray-500">
                  <svg className="h-5 w-5" viewBox="0 0 23 23" fill="currentColor">
                    <rect width="11" height="11" fill="#f25022" />
                    <rect x="12" width="11" height="11" fill="#00a4ef" />
                    <rect y="12" width="11" height="11" fill="#ffb900" />
                    <rect x="12" y="12" width="11" height="11" fill="#7fba00" />
                  </svg>
                  <span className="text-xs">Microsoft Azure AD</span>
                </div>
              </div>
            </div>

            {/* Terms */}
            <p className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
              By signing in, you agree to our{' '}
              <a href="#" className="text-primary-600 hover:underline dark:text-primary-400">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-primary-600 hover:underline dark:text-primary-400">
                Privacy Policy
              </a>
            </p>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Need help?{' '}
              <a
                href="#"
                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
